import React, { Component } from 'react';
import Axios from 'axios';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import Select from 'react-select';
import PropTypes from 'prop-types';
import cn from '@/utils/cn';
import PasswordRequirements from '@/components/shared/PasswordRequirements';
import { strictPasswordValidationError } from '@/components/shared/PasswordRequirements/helpers';
import { convertCopyright, renderErrors, RequiredAsterisk, SubmitError } from '../../../common/Forms/Utils';
import { Footer } from '../../../common/Modal';
import Tooltip from '../../../common/Tooltip';

import styles from '../Form.module.scss';

export default class AddForm extends Component {
  static propTypes = {
    classroomsPath: PropTypes.string.isRequired,
    closeModal: PropTypes.func.isRequired,
    createPath: PropTypes.string.isRequired,
    hasStrictPasswordRequirement: PropTypes.bool.isRequired,
    updateTable: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    this.state = {
      submitting: false,
      classroomId: null,
      classes: [],
      errorMessage: 'Please enter the email of a teacher with classes',
      loadingClasses: false
    };
  }

  getClasses(e) {
    this.setState({ classes: [], loadingClasses: true });
    Axios
      .get(this.props.classroomsPath.concat(e.target.value))
      .then((response) => {
        if (!response.data.length) {
          this.setState({ errorMessage: 'Could not find classes for this teacher. Please enter another email.' });
        }
        else {
          this.setState({ classes: response.data });
        }
      })
      .catch(() => {
        this.setState({ errorMessage: 'Could not find classes for this teacher. Please enter another email.' });
      })
      .then(() => this.setState({ loadingClasses: false }));
  }

  getClassSelectOptions() {
    return this.state.classes.map(classroom => ({ value: classroom.id, label: convertCopyright(classroom.name) }));
  }

  handleSubmit = async (values) => {
    this.setState({ submitting: true });

    return Axios
      .post(this.props.createPath, { student: values })
      .then((response) => {
        this.props.updateTable(response.data.data, 'add');
        this.props.closeModal();
      })
      .catch((error) => {
        this.setState({ submitting: false });
        return { [FORM_ERROR]: renderErrors(error.response.data.errors) };
      });
  };

  validation(values) {
    const errors = {};
    if (!values.teacher_email) {
      errors.teacher_email = 'Please complete all required fields.';
    }
    if (!this.state.classroomId) {
      errors.classRoom = 'Please complete all required fields.';
    }
    if (!values.first_name) {
      errors.first_name = 'Please complete all required fields.';
    }
    if (!values.last_name) {
      errors.last_name = 'Please complete all required fields.';
    }
    if (!values.username) {
      errors.username = 'Please complete all required fields.';
    }
    if (!values.password) {
      errors.password = 'Please complete all required fields.';
    }
    else if (values.password !== values.password_confirmation) {
      errors.password_confirmation = 'Password must match';
    }
    else if (this.props.hasStrictPasswordRequirement) {
      const message = strictPasswordValidationError(values.password);
      if (message) errors.password = message;
    }
    return errors;
  }

  renderSelectClasses(form) {
    return (
      <Field
        name="initial_classroom_id"
        render={() => (
          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="initial_classroom_id">
              Class<RequiredAsterisk />
            </label>
            <Select
              required
              searchable
              className={styles.input}
              clearable={false}
              id="initial_classroom_id"
              options={this.getClassSelectOptions()}
              value={this.state.classroomId}
              onChange={form.mutators.handleSelect}
            />
          </div>
        )}
      />
    );
  }

  renderSelectClassesDisabled() {
    return (
      <Tooltip content={this.state.errorMessage}>
        <Field
          name="initial_classroom_id"
          render={() => (
            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="initial_classroom_id">
                Class<RequiredAsterisk />
              </label>
              <Select
                disabled
                required
                className={styles.input}
                clearable={false}
                id="initial_classroom_id"
              />
            </div>
          )}
        />
      </Tooltip>
    );
  }

  render() {
    return (
      <Form
        mutators={{
          handleSelect: (args, state, utils) => {
            this.setState({ classroomId: args[0].value });
            utils.changeValue(state, 'initial_classroom_id', () => args[0].value);
          }
        }}
        render={({
          form, handleSubmit, hasValidationErrors, hasSubmitErrors, dirtySinceLastSubmit, submitError, errors, values
        }) => (
          <form onSubmit={handleSubmit}>
            <span className="sr-only">Fields marked with an asterisk (*) are required.</span>

            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="teacher_email">
                Teacher Email<RequiredAsterisk />
              </label>
              <Field
                className={styles.textInput}
                component="input"
                id="teacher_email"
                name="teacher_email"
                onBlur={e => this.getClasses(e)}
              />
            </div>

            {(this.state.classes.length || this.state.loadingClasses) ?
              this.renderSelectClasses(form) : this.renderSelectClassesDisabled() }

            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="first_name">
                First Name <RequiredAsterisk />
              </label>
              <Field
                className={styles.textInput}
                component="input"
                id="first_name"
                name="first_name"
              />
            </div>

            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="last_name">
                Last Name<RequiredAsterisk />
              </label>
              <Field
                className={styles.textInput}
                component="input"
                id="last_name"
                name="last_name"
              />
            </div>

            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="username">
                Username<RequiredAsterisk />
              </label>
              <Field
                className={styles.textInput}
                component="input"
                id="username"
                name="username"
                parse={val => (val === null ? '' : val)}
              />
            </div>

            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="password">
                New Password<RequiredAsterisk />
              </label>
              <Field
                autoComplete="new-password"
                className={styles.textInput}
                component="input"
                id="password"
                name="password"
                type="password"
              />
            </div>

            {this.props.hasStrictPasswordRequirement && (
              <PasswordRequirements
                maxWidth={330}
                password={values.password || ''}
                spacerWidth={140}
                wrapperClassName={cn({ 'tw-mt-[-10px] tw-mb-2': (values.password || '').length > 0 })}
              />
            )}

            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="password_confirmation">
                Confirm Password<RequiredAsterisk />
              </label>
              <Field
                autoComplete="new-password-confirmation"
                className={styles.textInput}
                component="input"
                id="password_confirmation"
                name="password_confirmation"
                type="password"
              />
            </div>

            <SubmitError error={submitError} />

            <Footer
              disableWithTooltipText={errors[Object.keys(errors)[0]]}
              primaryButtonDisabled={hasValidationErrors || (hasSubmitErrors && !dirtySinceLastSubmit)}
              secondaryButtonCallback={this.props.closeModal}
              submitting={this.state.submitting}
            />
          </form>
        )}
        validate={values => this.validation(values)}
        onSubmit={this.handleSubmit}
      />
    );
  }
}
